import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useForm, Controller, FormProvider } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useAuth } from "../../lib/AuthContext";
import XanoAPI from "../../lib/XanoAPI";
import { ClientType, TeamMember } from "../../types";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Checkbox } from "../ui/checkbox";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "../ui/form";
import { useToast } from "../ui/use-toast";
import { Copy, User, Users, CheckCircle2, Info } from "lucide-react";
import { Card, CardContent } from "../ui/card";
import { Separator } from "../ui/separator";

const api = XanoAPI.getInstance();

// Form validation schema
const copyClientSchema = z.object({
  targetUserId: z.number().min(1, "Please select a team member"),
  newClientName: z.string().min(1, "Client name is required").min(2, "Client name must be at least 2 characters"),
  includePersonalInfo: z.boolean(),
  includeFinancialInfo: z.boolean(),
  includeCrmData: z.boolean(),
  includePolicyInfo: z.boolean(),
  includeComparisons: z.boolean(),
  createAsTemplate: z.boolean(),
}).refine(
  (data) => 
    // Personal info is always included, so just check if at least one other category is selected
    data.includeFinancialInfo || 
    data.includeCrmData || 
    data.includePolicyInfo || 
    data.includeComparisons,
  {
    message: "Please select at least one additional category to copy (Personal Information is always included)",
    path: ["includeFinancialInfo"],
  }
);

type CopyClientFormData = z.infer<typeof copyClientSchema>;

interface CopyClientModalProps {
  client: ClientType;
  trigger?: React.ReactNode;
  onSuccess?: (newClientId: number) => void;
}

export function CopyClientModal({ 
  client, 
  trigger, 
  onSuccess 
}: CopyClientModalProps) {
  const auth = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Get team members from user_agency structure
  const currentUser = auth?.currentUser;
  const userAgency = currentUser?.user_agency?.[0];
  const agencyMembers = userAgency?.members || [];
  const isAdmin = currentUser?.user_permissions?.[0]?.role === "admin";

  // Transform agency members to TeamMember format
  const teamMembers = agencyMembers.map((member: any) => ({
    id: parseInt(member.id),
    name: member.name,
    email: member.email,
    role: userAgency?.agency_creator === parseInt(member.id) ? "admin" : "agent",
    status: member.status,
    permissions: member.user_permissions,
    permissions_id: member.permissions_id,
  }));

  // Filter team members based on permissions
  const availableAgents = isAdmin 
    ? teamMembers // Admin can assign to anyone
    : teamMembers.filter((m: any) => m.id === currentUser?.id); // Agent can only copy to self

  const methods = useForm<CopyClientFormData>({
    resolver: zodResolver(copyClientSchema),
    mode: "onChange",
    defaultValues: {
      targetUserId: 0,
      newClientName: `${client.firstName || ''} ${client.lastName || ''} (Copy)`.trim(),
      includePersonalInfo: true,
      includeFinancialInfo: true,
      includeCrmData: true,
      includePolicyInfo: true,
      includeComparisons: true,
      createAsTemplate: false,
    },
  });

  const {
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors, isValid },
  } = methods;

  const createAsTemplate = watch("createAsTemplate");

  // Personal information is always included
  useEffect(() => {
    setValue("includePersonalInfo", true);
  }, [setValue]);

  const onSubmit = async (data: CopyClientFormData) => {
    if (!client || !client.id) {
      toast({
        title: "Error",
        description: "Client ID is missing.",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);

    try {
      // Prepare copy data
      const copiedData: Partial<ClientType> = {
        user_id: data.targetUserId,
        status: "Pending", // New copied clients start as Pending
      };

      // Parse name from input (required fields)
      const [firstName, ...lastNameParts] = data.newClientName.split(' ');
      const lastName = lastNameParts.join(' ').replace(' (Copy)', '').trim();

      // Always set name fields (required for API)
      if (data.createAsTemplate) {
        // Template mode: use template naming
        copiedData.firstName = "Template";
        copiedData.lastName = `from ${client.firstName} ${client.lastName}`;
      } else {
        // Use name from input
        copiedData.firstName = firstName;
        copiedData.lastName = lastName || client.lastName || "Client";
      }

      // Include Personal Information (beyond name)
      if (data.includePersonalInfo && !data.createAsTemplate) {
        copiedData.phoneNumber = client.phoneNumber;
        copiedData.mobilePhone = client.mobilePhone;
        copiedData.email = client.email;
        copiedData.dateOfBirth = client.dateOfBirth;
        copiedData.gender = client.gender;
        copiedData.stateProvince = client.stateProvince;
        copiedData.residenceType = client.residenceType;
        copiedData.address = client.address;
        copiedData.city = client.city;
        copiedData.zipCode = client.zipCode;
        copiedData.spousesName = client.spousesName;
        copiedData.spousesDateOfBirth = client.spousesDateOfBirth;
        copiedData.dependents = client.dependents;
      }

      // Include Financial Information
      if (data.includeFinancialInfo) {
        copiedData.type = client.type;
        copiedData.targetRetirementAge = client.targetRetirementAge;
        copiedData.targetRetirementMonth = client.targetRetirementMonth;
        copiedData.lifeExpectancyAge = client.lifeExpectancyAge;
        copiedData.interestRatePriorToRetirement = client.interestRatePriorToRetirement;
        copiedData.interestRateInRetirement = client.interestRateInRetirement;
        copiedData.annuallyIncreaseSavingsWithRaises = client.annuallyIncreaseSavingsWithRaises;
        copiedData.grossIncome = client.grossIncome;
        copiedData.payPeriodsAnnual = client.payPeriodsAnnual;
        copiedData.expectedAnnualRaise = client.expectedAnnualRaise;
        copiedData.startPayments = client.startPayments;
        copiedData.calculationMethod = client.calculationMethod;
        copiedData.yearsIncomeSsi = client.yearsIncomeSsi;
        copiedData.maximumRetirementCola = client.maximumRetirementCola;
        copiedData.deductions = client.deductions;
        copiedData.additionalIncome = client.additionalIncome;

        // Federal-specific fields
        if (client.type === "federal") {
          copiedData.retirementPlan = client.retirementPlan;
          copiedData.fersPlan = client.fersPlan;
          copiedData.serviceComputationDate = client.serviceComputationDate;
          copiedData.contributionMethod = client.contributionMethod;
          copiedData.pretaxCurrentBalance = client.pretaxCurrentBalance;
          copiedData.contributionPerPay = client.contributionPerPay;
          copiedData.preretireRateOfReturn = client.preretireRateOfReturn;
          copiedData.distributionMethod = client.distributionMethod;
          copiedData.distributionStartMethod = client.distributionStartMethod;
          copiedData.allocationMethod = client.allocationMethod;
          copiedData.pretax_currentBalance = client.pretax_currentBalance;
          copiedData.pretax_contributionPerPaycheck = client.pretax_contributionPerPaycheck;
          copiedData.roth_currentBalance = client.roth_currentBalance;
          copiedData.roth_contributionPerPaycheck = client.roth_contributionPerPaycheck;
          copiedData.fegli_basicCoverage = client.fegli_basicCoverage;
          copiedData.unused_sick_leave = client.unused_sick_leave;
        }

        // 401k-specific fields
        if (client.type === "401k") {
          copiedData.t_401k_type = client.t_401k_type;
          copiedData.t_401k_currentSalary = client.t_401k_currentSalary;
          copiedData.t_401k_payPeriodsPerYear = client.t_401k_payPeriodsPerYear;
          copiedData.t_401k_monthlyContributionPercent = client.t_401k_monthlyContributionPercent;
          copiedData.t_401k_employerMatchPercent = client.t_401k_employerMatchPercent;
          copiedData.t_401k_existing401kValue = client.t_401k_existing401kValue;
        }

        // 403b-specific fields
        if (client.type === "403b") {
          copiedData.t_403b_currentAge = client.t_403b_currentAge;
          copiedData.t_403b_plannedRetirementAge = client.t_403b_plannedRetirementAge;
          copiedData.t_403b_currentBalance = client.t_403b_currentBalance;
          copiedData.t_403b_annualContribution = client.t_403b_annualContribution;
          copiedData.t_403b_employerMatchPercentage = client.t_403b_employerMatchPercentage;
        }
      }

      // Include CRM Data
      if (data.includeCrmData) {
        copiedData.lifecycleStage = client.lifecycleStage;
        copiedData.lastContactDate = client.lastContactDate;
        copiedData.preferredContactMethod = client.preferredContactMethod;
        copiedData.contactSource = client.contactSource;
        copiedData.federalAgency = client.federalAgency;
      }

      // Include Policy Information
      if (data.includePolicyInfo) {
        copiedData.policy_provider = client.policy_provider;
        copiedData.annuity_type = client.annuity_type;
        copiedData.rollover_amount = client.rollover_amount;
        copiedData.policy_start_date = client.policy_start_date;
        copiedData.policy_end_date = client.policy_end_date;
        copiedData.policy_status = client.policy_status;
        copiedData.agent_commission = client.agent_commission;
      }

      // Include Comparisons
      if (data.includeComparisons) {
        copiedData.comparisons = client.comparisons;
      }

      // Create the new client
      const newClient = await api.addClient(copiedData as ClientType);

      setIsLoading(false);
      setIsOpen(false);

      toast({
        title: "Client Copied Successfully!",
        description: `${data.newClientName} has been created and assigned to the selected team member.`,
      });

      // Navigate to the new client or call success callback
      if (onSuccess && newClient.id) {
        onSuccess(Number(newClient.id));
      } else if (newClient.id) {
        navigate(`/edit-client/${newClient.id}`);
      }
    } catch (error) {
      console.error("Error copying client:", error);
      setIsLoading(false);
      
      toast({
        title: "Copy Failed",
        description: error instanceof Error ? error.message : "Failed to copy client. Please try again.",
        variant: "destructive",
      });
    }
  };

  // Get client count for each team member (optional enhancement)
  const getAgentClientCount = (agentId: number) => {
    // This could be enhanced to show actual client counts
    return ""; // For now, leave empty
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button  className="copy-client-button">
            <Copy className="mr-2 h-4 w-4" />
            Copy Client
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[650px] max-h-[85vh] overflow-y-auto">
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <DialogHeader className="space-y-3">
              <div className="flex items-center gap-2">
                <Copy className="h-5 w-5 text-accent" />
                <DialogTitle className="text-xl">Copy Client</DialogTitle>
              </div>
              <DialogDescription className="text-base">
                Create a duplicate of this client and assign it to a team member in your organization.
              </DialogDescription>
            </DialogHeader>

            <Separator className="my-4" />

            <div className="grid gap-5 py-2">
              {/* Client Info Card - Improved */}
              <div className="bg-gradient-to-r from-gray-50 to-white border border-gray-200 rounded-lg p-4">
                <div className="flex items-center gap-4">
                  <div className="bg-white rounded-full p-3 shadow-sm">
                    <User className="h-6 w-6 text-accent" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900 text-base">
                      {client.firstName} {client.lastName}
                    </p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-sm text-gray-600">
                        {client.type?.toUpperCase()} Plan
                      </span>
                      {client.status && (
                        <>
                          <span className="text-gray-400">•</span>
                          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                            client.status === 'Active' ? 'bg-green-100 text-green-700' :
                            client.status === 'Pending' ? 'bg-yellow-100 text-yellow-700' :
                            'bg-gray-100 text-gray-700'
                          }`}>
                            {client.status}
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Target Agent Selection - Improved */}
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-gray-600" />
                  <Label htmlFor="targetUserId" className="text-sm font-semibold text-gray-700">
                    Assign to Team Member <span className="text-red-500">*</span>
                  </Label>
                </div>
                <FormControl>
                  <Controller
                    name="targetUserId"
                    control={control}
                    render={({ field: { onChange, value }, fieldState: { error } }) => {
                      const selectedMember = availableAgents.find((m: any) => m.id === value);
                      
                      return (
                        <>
                          <Select
                            onValueChange={(val) => onChange(Number(val))}
                            value={value ? String(value) : ""}
                          >
                            <SelectTrigger className="w-full h-11 border-gray-300 focus:border-accent">
                              <SelectValue placeholder="Choose a team member...">
                                {selectedMember ? (
                                  <div className="flex items-center gap-2">
                                    <User className="h-4 w-4 text-gray-400 flex-shrink-0" />
                                    <span className="font-medium text-gray-900 truncate">
                                      {selectedMember.name || `User ${selectedMember.id}`}
                                    </span>
                                    {selectedMember.id === currentUser?.id && (
                                      <span className="text-xs font-medium bg-gray-100 text-accent px-2 py-0.5 rounded flex-shrink-0">
                                        You
                                      </span>
                                    )}
                                        {selectedMember.role && (
                                          <span className={`text-xs font-medium px-2 py-0.5 rounded capitalize flex-shrink-0 ${
                                            selectedMember.role === 'admin' 
                                              ? 'bg-gray-100 text-accent' 
                                              : 'bg-gray-100 text-gray-700'
                                          }`}>
                                            {selectedMember.role}
                                          </span>
                                        )}
                                  </div>
                                ) : (
                                  "Choose a team member..."
                                )}
                              </SelectValue>
                            </SelectTrigger>
                            <SelectContent className="max-w-[400px]">
                            <SelectGroup>
                              {availableAgents.length === 0 ? (
                                <SelectItem value="0" disabled>
                                  No team members available
                                </SelectItem>
                              ) : (
                                availableAgents.map((member: any) => (
                                  <SelectItem 
                                    key={member.id} 
                                    value={String(member.id)}
                                    className="cursor-pointer hover:bg-gray-50 focus:bg-gray-100 transition-colors py-3 px-3"
                                  >
                                    <div className="flex flex-col gap-1 w-full">
                                      <div className="flex items-center gap-2 w-full">
                                        <User className="h-4 w-4 text-gray-400 flex-shrink-0" />
                                        <span className="font-medium text-gray-900 truncate flex-1">
                                          {member.name || `User ${member.id}`}
                                        </span>
                                        {member.id === currentUser?.id && (
                                          <span className="text-xs font-medium bg-gray-100 text-accent px-2 py-0.5 rounded flex-shrink-0">
                                            You
                                          </span>
                                        )}
                                        {member.role && (
                                          <span className={`text-xs font-medium px-2 py-0.5 rounded capitalize flex-shrink-0 ${
                                            member.role === 'admin' 
                                              ? 'bg-gray-100 text-accent' 
                                              : 'bg-gray-100 text-gray-700'
                                          }`}>
                                            {member.role}
                                          </span>
                                        )}
                                      </div>
                                      {member.email && (
                                        <div className="flex items-center gap-2 pl-6">
                                          <span className="text-xs text-gray-500 truncate">
                                            {member.email}
                                          </span>
                                        </div>
                                      )}
                                    </div>
                                  </SelectItem>
                                ))
                              )}
                            </SelectGroup>
                          </SelectContent>
                        </Select>
                        {error && (
                          <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                            <Info className="h-3 w-3" />
                            {error.message}
                          </p>
                        )}
                      </>
                    );
                  }}
                  />
                </FormControl>
                {!isAdmin && (
                  <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded px-3 py-2 flex items-start gap-2">
                    <Info className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    <span>As an agent, you can only copy clients to yourself. Contact an admin to assign to other team members.</span>
                  </p>
                )}
              </div>

              {/* What to Copy - Improved */}
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-gray-600" />
                  <Label className="text-sm font-semibold text-gray-700">
                    Select Additional Data to Copy
                  </Label>
                </div>
                <p className="text-xs text-gray-600">
                  Personal Information is always included
                </p>
                <Card className="border-gray-200">
                  <CardContent className="pt-5 pb-4">
                  <div className="space-y-3">
                    {/* Personal Information - Always included */}
                    <div className="flex items-start space-x-3 p-3 rounded-lg bg-gray-50 border border-gray-200">
                      <div className="bg-accent rounded p-1 mt-0.5">
                        <svg className="h-3 w-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <div className="flex-1">
                        <div className="text-sm font-medium leading-none text-gray-900">
                          Personal Information
                          <span className="ml-2 text-xs font-normal text-accent">(Always Included)</span>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          Name, DOB, Contact, Address
                        </p>
                      </div>
                    </div>

                    <Controller
                      name="includeFinancialInfo"
                      control={control}
                      render={({ field }) => (
                        <div className={`flex items-start space-x-3 p-3 rounded-lg transition-colors ${
                          field.value 
                            ? 'border border-green-200 bg-[hsl(120_60%_97%)]' 
                            : 'border border-transparent hover:bg-gray-50'
                        }`}>
                          <Checkbox
                            id="includeFinancialInfo"
                            checked={field.value}
                            onCheckedChange={field.onChange}
                            className="mt-1"
                          />
                          <div className="flex-1">
                            <label
                              htmlFor="includeFinancialInfo"
                              className="text-sm font-medium leading-none cursor-pointer text-gray-900"
                            >
                              Financial Information
                            </label>
                            <p className="text-xs text-gray-500 mt-1">
                              Retirement plans, TSP, Income
                            </p>
                          </div>
                        </div>
                      )}
                    />

                    <Controller
                      name="includeCrmData"
                      control={control}
                      render={({ field }) => (
                        <div className={`flex items-start space-x-3 p-3 rounded-lg transition-colors ${
                          field.value 
                            ? 'border border-green-200 bg-[hsl(120_60%_97%)]' 
                            : 'border border-transparent hover:bg-gray-50'
                        }`}>
                          <Checkbox
                            id="includeCrmData"
                            checked={field.value}
                            onCheckedChange={field.onChange}
                            className="mt-1"
                          />
                          <div className="flex-1">
                            <label
                              htmlFor="includeCrmData"
                              className="text-sm font-medium leading-none cursor-pointer text-gray-900"
                            >
                              CRM Data
                            </label>
                            <p className="text-xs text-gray-500 mt-1">
                              Lifecycle stage, Contact history
                            </p>
                          </div>
                        </div>
                      )}
                    />

                    <Controller
                      name="includePolicyInfo"
                      control={control}
                      render={({ field }) => (
                        <div className={`flex items-start space-x-3 p-3 rounded-lg transition-colors ${
                          field.value 
                            ? 'border border-green-200 bg-[hsl(120_60%_97%)]' 
                            : 'border border-transparent hover:bg-gray-50'
                        }`}>
                          <Checkbox
                            id="includePolicyInfo"
                            checked={field.value}
                            onCheckedChange={field.onChange}
                            className="mt-1"
                          />
                          <div className="flex-1">
                            <label
                              htmlFor="includePolicyInfo"
                              className="text-sm font-medium leading-none cursor-pointer text-gray-900"
                            >
                              Policy Information
                            </label>
                            <p className="text-xs text-gray-500 mt-1">
                              Insurance, Commission details
                            </p>
                          </div>
                        </div>
                      )}
                    />

                    <Controller
                      name="includeComparisons"
                      control={control}
                      render={({ field }) => (
                        <div className={`flex items-start space-x-3 p-3 rounded-lg transition-colors ${
                          field.value 
                            ? 'border border-green-200 bg-[hsl(120_60%_97%)]' 
                            : 'border border-transparent hover:bg-gray-50'
                        }`}>
                          <Checkbox
                            id="includeComparisons"
                            checked={field.value}
                            onCheckedChange={field.onChange}
                            className="mt-1"
                          />
                          <div className="flex-1">
                            <label
                              htmlFor="includeComparisons"
                              className="text-sm font-medium leading-none cursor-pointer text-gray-900"
                            >
                              Comparisons
                            </label>
                            <p className="text-xs text-gray-500 mt-1">
                              Financial scenarios
                            </p>
                          </div>
                        </div>
                      )}
                    />
                  </div>
                  {errors.includePersonalInfo && (
                    <p className="text-red-500 text-xs mt-2 flex items-center gap-1">
                      <Info className="h-3 w-3" />
                      {errors.includePersonalInfo.message}
                    </p>
                  )}
                </CardContent>
              </Card>
              </div>

              {/* Advanced Options - Improved */}
              <Card className="border-gray-200 bg-gray-50">
                <CardContent className="pt-5 pb-4">
                  <Controller
                    name="createAsTemplate"
                    control={control}
                    render={({ field }) => (
                      <div className="flex items-start space-x-3 p-3 rounded-lg bg-white border border-gray-200">
                        <Checkbox
                          id="createAsTemplate"
                          checked={field.value}
                          onCheckedChange={field.onChange}
                          className="mt-1"
                        />
                        <div className="flex-1">
                          <label
                            htmlFor="createAsTemplate"
                            className="text-sm font-semibold leading-none cursor-pointer text-gray-900 flex items-center gap-2"
                          >
                            Save as Reusable Template
                            <span className="text-xs font-normal bg-gray-100 text-accent px-2 py-0.5 rounded">Advanced</span>
                          </label>
                          <p className="text-xs text-gray-700 mt-2 leading-relaxed font-medium">
                            Perfect for creating multiple clients with similar financial profiles
                          </p>
                          <div className="mt-3 space-y-2 pl-1">
                            <div className="flex items-start gap-2">
                              <svg className="h-3.5 w-3.5 text-green-600 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                              <p className="text-xs text-gray-600">
                                <span className="font-medium text-gray-700">Keeps:</span> All retirement plans, income settings, TSP contributions, and calculation methods
                              </p>
                            </div>
                            <div className="flex items-start gap-2">
                              <svg className="h-3.5 w-3.5 text-red-500 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                              <p className="text-xs text-gray-600">
                                <span className="font-medium text-gray-700">Removes:</span> Name, date of birth, phone, email, address, and spouse information
                              </p>
                            </div>
                          </div>
                          <div className="mt-3 p-2.5 bg-blue-50 border border-blue-200 rounded-lg">
                            <p className="text-xs text-blue-800 leading-relaxed">
                              <span className="font-semibold">Use Case Example:</span> Create a template for "Federal FERS employees retiring at 62" then quickly add new clients by filling in just their personal details.
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  />
                </CardContent>
              </Card>

              {/* New Client Name - Improved */}
              <div className="space-y-2">
                <Label htmlFor="newClientName" className="text-sm font-semibold text-gray-700">
                  New Client Name <span className="text-red-500">*</span>
                </Label>
                <FormControl>
                  <Controller
                    name="newClientName"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <>
                        <Input
                          {...field}
                          id="newClientName"
                          placeholder="Enter client name..."
                          className="w-full h-11 border-gray-300 focus:border-accent"
                        />
                        <p className="text-xs text-gray-500 mt-1.5">
                          This name will appear in the client list.
                        </p>
                        {error && (
                          <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                            <Info className="h-3 w-3" />
                            {error.message}
                          </p>
                        )}
                      </>
                    )}
                  />
                </FormControl>
              </div>

              {/* Info message - Improved */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3.5 flex items-start gap-3">
                <Info className="h-4 w-4 text-accent mt-0.5 flex-shrink-0" />
                <p className="text-sm text-gray-700 leading-relaxed">
                  The copied client will be created with status "Pending" and can be edited immediately after creation.
                </p>
              </div>
            </div>

            <Separator className="mt-6" />
            
            <DialogFooter className="mt-6 gap-2">
              <Button 
                type="button" 
                // variant="outline" 
                onClick={() => setIsOpen(false)}
                disabled={isLoading}
                className="min-w-[100px]"
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                className="bg-accent hover:opacity-90 min-w-[140px]"
                disabled={!isValid || isLoading}
              >
                {isLoading ? (
                  <>
                    <span className="animate-spin mr-2">⏳</span>
                    Creating...
                  </>
                ) : (
                  <>
                    <Copy className="mr-2 h-4 w-4" />
                    Create Copy
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        </FormProvider>
      </DialogContent>
    </Dialog>
  );
}

